// utils/squareImage.ts
import { AnimationHistoryEntry } from "../../../backend/useAnimate";
import { uploadBlobToStorage } from "../../../backend/storageHelpers";
import { supabase } from "../../../backend/supabase";

/**
 * Ensures the reference image is a perfect square.
 * If not, it creates a new square canvas based on the largest dimension 
 * and centers the original image on a white background.
 */
export const squareImage = async (
    currentEntry: AnimationHistoryEntry,
    animationId: string,
    createHistoryEntry: any
): Promise<AnimationHistoryEntry | null> => {
    try {
        const imageUrl = currentEntry.reference_image;
        
        // 1. Load the original image to get dimensions
        const img = new Image();
        img.crossOrigin = "anonymous";
        
        await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            img.src = imageUrl;
        });

        const originalWidth = img.naturalWidth;
        const originalHeight = img.naturalHeight;

        // 2. Check if it's already a square
        if (originalWidth === originalHeight) {
            // console.log('Image is already square, skipping transformation.');
            return currentEntry;
        }

        // 3. Determine the size of the square (largest dimension)
        const squareSize = Math.max(originalWidth, originalHeight);

        // 4. Setup Canvas
        const canvas = document.createElement("canvas");
        canvas.width = squareSize;
        canvas.height = squareSize;
        const ctx = canvas.getContext("2d");

        if (!ctx) {
            throw new Error("Could not get canvas context");
        }

        // 5. Draw white background
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, squareSize, squareSize);

        // 6. Calculate centering offsets
        const xOffset = (squareSize - originalWidth) / 2;
        const yOffset = (squareSize - originalHeight) / 2;

        // 7. Draw original image centered
        ctx.drawImage(img, xOffset, yOffset, originalWidth, originalHeight);

        // 8. Convert to Blob and Upload
        const blob = await new Promise<Blob | null>((resolve) => {
            canvas.toBlob((b) => resolve(b), "image/png");
        });

        if (!blob) {
            throw new Error("Failed to create blob from canvas");
        }

        const STORAGE_FOLDER = 'animation-assets';
        const uploadedImageUrl = await uploadBlobToStorage(
            supabase,
            blob,
            'square-reference-image',
            STORAGE_FOLDER,
            'image/png'
        );

        // 9. Create new history entry with the squared reference image
        const newEntry = await createHistoryEntry(
            animationId,
            currentEntry.user_prompt,
            uploadedImageUrl,
            currentEntry.animation_images || [],
            currentEntry.enhanced_prompt || undefined,
            currentEntry.animation_video || undefined,
            currentEntry.reference_image_description || undefined
        );

        return newEntry;

    } catch (error) {
        console.error("Error in squareImage utility:", error);
        return null;
    }
};